import React, { useEffect, useMemo, useState } from "react";
import { BALANCE_RELOAD, COMMON_WEB_PATH } from "../../constant";
import { Box, Image } from "@chakra-ui/react";
import StartGameButton from "../../pages/Home/StartGameButton";
import { actionLogout, clearCashRegister } from "../../api/postApi";
import LoadingPage from "../Loading/LoadingPage";
import { useDispatch, useSelector } from "react-redux";
import ClearCashConfirm from "./ClearCashConfirm";
import EndGameDetail from "../EndGameDetail/EndGameDetail";
import { AnimatePresence } from "framer-motion";
import useMyBalance from "../../hook/useMyBalance";
import useCheckLogin from "../../hook/useCheckLogin";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import ClearOrOnline from "./ClearOrOnline";

const ClearCashRegisterButton = () => {
  const walletCurrency = useSelector((state) => state.isWalletCurrency); //從redux store中取isWalletCurrency的值 存到walletCurrency變數中
  const [showStartGame, setShowStartGame] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showEndGame, setShowEndGame] = useState(false);
  const [savedBalance, setSavedBalance] = useState(null); // 儲存棄分前的餘額
  const [checked, setChecked] = useState(false); // 是否勾選
  const [transCode, setTransCode] = useState(""); // 當使用點數轉移時，儲存轉移的回傳值，要用來轉成QRcode
  const [withdrawalNumber, setWithdrawalNumber] = useState(""); //訂單號碼
  const [showClearOrOnline, setShowClearOrOnline] = useState(false); // 是否顯示棄分或轉線上選項

  const { isMachineUser } = useCheckLogin();
  const { images } = useWebsiteInfo();

  const currentBalance = useMyBalance();
  const dispatch = useDispatch();

  const memberData = useSelector((state) => state.isMemberInfo);
  const canTransferPoints = memberData?.open_trans_point === 1; //是否可以轉移點數

  // 監聽 Socket 訊息和轉移成功狀態
  const isTriggerGivenUpPoint = useSelector(
    (state) => state.isTriggerGivenUpPoint
  );
  const isSocketMessage = useSelector((state) => state.isSocketMessage);

  useEffect(() => {
    if (showConfirm) {
      dispatch({ type: "GET_BALANCE" });
    }
  }, [showConfirm, dispatch]);

  // 監聽轉移成功，更新訂單號碼
  useEffect(() => {
    if (isTriggerGivenUpPoint && isSocketMessage?.memo?.withdrawal_number) {
      // 更新訂單號碼為 Socket 回傳的新訂單號碼
      setWithdrawalNumber(isSocketMessage.memo.withdrawal_number);
      console.log("已更新訂單號碼:", isSocketMessage.memo.withdrawal_number);
    }
  }, [isTriggerGivenUpPoint, isSocketMessage]);

  const handleClearCashRegister = async () => {
    if (isProcessing) return;
    setIsProcessing(true); //防止重複點擊

    try {
      const data = await clearCashRegister({
        point_transfer: checked, //是否使用點數轉移
      });

      const { balance, withdrawal_number, trans_code = "" } = data || {};
      setSavedBalance(balance || 0);
      setWithdrawalNumber(withdrawal_number || ""); //存 傳來的訂單號碼

      dispatch({
        type: "MANUAL_UPDATE_BALANCE",
        payload: {
          currency: walletCurrency,
          amount: 0,
        },
      }); // 手動更新餘額到 Redux
      if (trans_code) {
        setTransCode(trans_code);
      }

      /* 原本這四個 setState 包在 setTimeout(..., 0) 裡。
         React 18 的 await 之後本來就會自動批次,不需要繞這一圈;
         而且延到下一個 tick 會讓「遮罩已收、結算畫面還沒開」中間空一幀,
         機台上看得出來閃一下。直接同步設,同一次 render 換過去。 */
      setShowConfirm(false);
      setShowEndGame(true);
      setShowClearOrOnline(false);
    } catch (error) {
      /* 這裡刻意「不」關彈窗。
         攔截器已經 dispatch API_ERROR → 會跳錯誤 toast,
         彈窗留著讓使用者看完訊息後自己決定重試或取消。
         遮罩由 finally 的 setIsProcessing(false) 收掉,不會卡住。 */
      console.error("棄分失敗:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // ClearCashConfirm 的確認完成處理
  const handleConfirmComplete = () => {
    // 不在這裡關閉 confirm modal，等 API 完成後在 handleClearCashRegister 中關閉
    handleClearCashRegister();
  };

  // 點擊棄分按鈕時，根據 canTransferPoints 決定顯示哪個組件
  const handleClick = () => {
    if (currentBalance == 0) return;

    if (canTransferPoints) {
      // 如果可以轉移點數，顯示選擇視窗
      setShowClearOrOnline(true);
    } else {
      // 如果不能轉移點數，顯示確認視窗
      setShowConfirm(true);
    }
  };

  // 取消棄分
  const handleCancel = () => {
    setShowConfirm(false);
  };

  // 處理選擇棄分
  const handleSelectClearCash = () => {
    setShowClearOrOnline(false);
    setShowConfirm(true);
  };

  // 處理選擇轉至線上
  const handleSelectGoOnline = async () => {
    if (isProcessing) return;
    setIsProcessing(true); // 防止重複點擊

    try {
      // 呼叫棄分 API，並強制開啟點數轉移
      const data = await clearCashRegister({
        point_transfer: true, // 強制設定為 true，模擬勾選點數轉移的行為
      });

      const { balance, withdrawal_number, trans_code = "" } = data || {};
      setSavedBalance(balance || 0);
      setWithdrawalNumber(withdrawal_number || "");

      dispatch({ type: "GET_BALANCE" });

      if (trans_code) {
        setTransCode(trans_code); // 設定 transCode，觸發 ClearCashButTransfer 顯示
      }

      // 同 handleClearCashRegister:React 18 會自動批次,不需要 setTimeout(..., 0)
      setShowEndGame(true); // 顯示 EndGameDetail
      setShowClearOrOnline(false); // 成功才關掉選擇視窗
    } catch (error) {
      /* 失敗時「不」關選擇視窗 —— 原本寫在 finally,成功失敗都關,
         使用者會看到視窗直接消失、只剩一個錯誤 toast,不知道該重試哪一步。
         留著讓他自己選重試或取消;遮罩由下面的 setIsProcessing(false) 收掉。 */
      console.error("轉至線上失敗:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 處理 EndGameDetail 關閉
  const handleEndGameClose = () => {
    setShowEndGame(false);
    setShowClearOrOnline(false); // 關閉 選棄分或轉至線上 的彈窗
    setTransCode(""); // 清空transCode
    setChecked(false); // 重設point_transfer狀態
  };

  return (
    <AnimatePresence>
      {/* 確認彈窗 */}
      {showConfirm && (
        <ClearCashConfirm
          onConfirm={handleConfirmComplete}
          onCancel={handleCancel}
          isProcessing={isProcessing}
          setChecked={setChecked}
          checked={checked}
        />
      )}

      {/* 選擇視窗 - 只有當 showClearOrOnline 為 true 時才顯示 */}
      {showClearOrOnline && (
        <ClearOrOnline
          onClearCash={handleSelectClearCash}
          onGoOnline={handleSelectGoOnline}
          onCancel={() => setShowClearOrOnline(false)}
          isProcessing={isProcessing}
        />
      )}

      {showEndGame && (
        <EndGameDetail
          onClose={handleEndGameClose}
          previousBalance={savedBalance}
          transCode={transCode}
          withdrawalNumber={withdrawalNumber}
        />
      )}

      {/* 棄分按鈕 */}
      <Box
        onClick={handleClick}
        position="relative"
        cursor={isProcessing || currentBalance == 0 ? "not-allowed" : "pointer"}
        display="flex"
        alignItems={"center"}
        justifyContent={"center"}
        shadow="sm"
      >
        <Image
          filter={currentBalance == 0 ? "grayscale(100%)" : ""}
          src={images.getREAL}
          height={{
            base: "90px",
            //xl: "90px",
            xl: isMachineUser ? "115px" : "90px",
          }}
          //minW="90px"
          minW={isMachineUser ? "110px" : "90px"}
        />
      </Box>

      {/* {showStartGame && <StartGameButton />} */}
    </AnimatePresence>
  );
};

export default ClearCashRegisterButton;
